import React, { useEffect, useMemo } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { chunk, keys, omit, toUpper } from 'lodash'
import { Box, Chip, Grid, Popover, Stack, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import useDebounce from 'utils/customHook/useDebounce'
import { CUMUL_PERSO_AVERAGE, CUMUL_PERSO_MAX, CUMUL_PERSO_MIN, CUMUL_PERSO_SUM } from 'utils/constants/MeasureConstants'
import { LARGE_RADIUS } from 'utils/constants/Theme'
import { brutRgbMainBlue, mainBlue, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import SimpleCheckbox from 'components/forms/SimpleCheckbox'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import { getI18nOrLabel } from 'utils/StringUtil'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded'
import NewRadioButtons from 'components/forms/NewRadioButtons'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import { FieldsetMUI } from 'components/StyledElements'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import SimpleInput, { INPUT_TYPES } from 'components/forms/SimpleInput'

export const FREQUENCY_STATIONS_TYPES = {
    piezo: 'PIEZO',
    hydro: 'HYDRO',
    pluvio: 'PLUVIO',
}

const DEFAULT_DISPLAY_MODES = {
    auto: true,
    brute: false,
    min: false,
    max: false,
    average: false,

    personalizedGrouping: false,
    personalizedGroupingValue: '',
}

const DEFAULT_PLUVIO_DISPLAY_MODES = {
    auto: true,
    sumHour: false,
    sumDay: false,
    sumWeek: false,
    sumDecade: false,
    sumMonth: false,
    sumYear: false,
    personalizedGrouping: false,
    default: 'auto', // mode pré-sélectionné dans le widget parmi les modes accessibles (booléens à true)
}

const MAX_POPOVER_HEIGHT = { xl: 600, lg: 400 }

const roundDivStyle = {
    width: '1rem',
    height: '1rem',
    borderRadius: '50rem',
}

const DAY = 'day'
const HOUR = 'hour'

const FrequencyDatasChoices = ({
    stationType = FREQUENCY_STATIONS_TYPES.piezo,
    defaultDisplayMode = [],

    displayModes = [],
    setDisplayModes = () => {},

    optionDayHourSelected = HOUR,
    setOptionDayHourSelected = () => {},
    hoursCumul = 1,
    setHoursCumul = () => {},
    optionCumulSelected = CUMUL_PERSO_MAX,
    setOptionCumulSelected = () => {},
}) => {
    const optionDayHour = [
        { value: HOUR, label: i18n.hour },
        { value: DAY, label: i18n.day },
    ]
    const optionCumul = [
        { value: CUMUL_PERSO_MAX, label: i18n.max },
        { value: CUMUL_PERSO_MIN, label: i18n.min },
        { value: CUMUL_PERSO_SUM, label: i18n.sum },
        { value: CUMUL_PERSO_AVERAGE, label: i18n.average },
    ]

    const isPluvio = stationType === FREQUENCY_STATIONS_TYPES.pluvio
    // Modes accessibles = cases cochées ; sert d'options pour le choix de l'affichage par défaut (pluvio).
    const accessibleModeOptions = keys(omit(displayModes, ['all', 'personalizedGroupingValue', 'default']))
        .filter(key => displayModes[key])
        .map(key => ({ value: key, label: key === 'auto' ? 'Auto' : getI18nOrLabel(key) }))

    const getDisplayModeColor = displayMode => {
        switch (displayMode) {
            case 'min':
                return PASTEL_COLOR.red
            case 'max':
                return PASTEL_COLOR.blue
            case 'average':
                return PASTEL_COLOR.green
            default:
                return 'black'
        }
    }

    return (
        <Stack spacing={2}>
            <SimpleCheckbox
                label='Auto'
                labelStyle={{ fontWeight: 'normal' }}
                beforeLabel={(
                    <Box sx={{ ...roundDivStyle, backgroundColor: 'black' }} />
                )}
                afterLabel={(
                    <Tooltip
                        placement='right'
                        title={i18n.descriptionCumulAuto}
                    >
                        <InfoRoundedIcon sx={{ fontSize: '18px', color: 'grey' }} />
                    </Tooltip>
                )}
                checked={displayModes.auto}
                onToggle={event => isPluvio ? setDisplayModes({ auto: event.target.checked }) : setDisplayModes(defaultDisplayMode)}
            />
            {keys(omit(displayModes, ['auto', 'all', 'personalizedGrouping', 'personalizedGroupingValue', 'default'])).map(key => (
                <SimpleCheckbox
                    key={key}
                    label={getI18nOrLabel(key)}
                    labelStyle={{ fontWeight: 'normal' }}
                    beforeLabel={(
                        <Box style={{ ...roundDivStyle, backgroundColor: getDisplayModeColor(key) }} />
                    )}
                    afterLabel={key === 'brute' && (
                        <WarningRoundedIcon sx={{ fontSize: '18px', color: 'orange' }} />
                    )}
                    checked={displayModes[key]}
                    onToggle={event => setDisplayModes({ [key]: event.target.checked })}
                />
            ))}
            <SimpleCheckbox
                label={i18n.personalizedGrouping}
                labelStyle={{ fontWeight: 'normal' }}
                beforeLabel={(
                    <Box style={{ ...roundDivStyle, backgroundColor: PASTEL_COLOR.orange }} />
                )}
                checked={displayModes.personalizedGrouping}
                onToggle={event => {
                    const day = `${optionCumulSelected}_${hoursCumul * 24}`
                    const hour = `${optionCumulSelected}_${hoursCumul}`
                    setDisplayModes({ personalizedGrouping: event.target.checked }, { personalizedGroupingValue: optionDayHourSelected === DAY ? day : hour })
                }}
            />
            {!!displayModes.personalizedGrouping && (
                <FieldsetMUI
                    title={(
                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ padding: '0 0.5rem' }}>
                            <QueryStatsRoundedIcon />
                            {i18n.modeGrouping}
                        </Stack>
                    )}
                >
                    <Grid container rowSpacing={1} justifyContent='center' sx={{ padding: '0.5rem 0 0' }}>
                        <Grid>
                            <NewRadioButtons
                                label={i18n.modeDaysHours}
                                value={optionDayHourSelected}
                                items={optionDayHour}
                                onChange={setOptionDayHourSelected}
                            />
                        </Grid>
                        <Grid container columnSpacing={1} size={12} sx={{ padding: '10px 10px' }}>
                            <Grid size={12}>
                                <SuperMultiAutocomplete
                                    label={i18n.modeGrouping}
                                    values={optionCumulSelected}
                                    keyValue='value'
                                    onChange={setOptionCumulSelected}
                                    options={optionCumul}
                                    disablePortal={false}
                                />
                            </Grid>
                            <Grid size={12}>
                                <SimpleInput
                                    title={i18n.numberHoursDays}
                                    value={hoursCumul}
                                    onChange={setHoursCumul}
                                    placeholder='1'
                                    type={INPUT_TYPES.int}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </FieldsetMUI>
            )}
            {isPluvio && (
                <SuperMultiAutocomplete
                    label={i18n.defaultDisplay}
                    values={displayModes.default}
                    keyValue='value'
                    options={accessibleModeOptions}
                    onChange={value => setDisplayModes({ default: value })}
                    disablePortal={false}
                />
            )}
        </Stack>
    )
}

FrequencyDatasChoices.propTypes = {
    stationType: PropTypes.string,
    defaultDisplayMode: PropTypes.arrayOf(PropTypes.shape({})),
    displayModes: PropTypes.arrayOf(PropTypes.shape({})),
    setDisplayModes: PropTypes.func,
    optionDayHourSelected: PropTypes.string,
    setOptionDayHourSelected: PropTypes.func,
    hoursCumul: PropTypes.number,
    setHoursCumul: PropTypes.func,
    optionCumulSelected: PropTypes.string,
    setOptionCumulSelected: PropTypes.func,
}

const FrequencyDatas = ({
    module = { module: 'OBSERVATOIRE' },
    stationType = FREQUENCY_STATIONS_TYPES.piezo,

    parameters = [],
    setParameters = () => {},
    getGlobalParameter = () => {},

    moduleParameters = [],
    setModuleParameters = [],
    getGlobalModuleParameter = () => {},

    displayModuleParameter = false,

    isGlobal = false,
}) => {
    const parameter = `FREQUENCE_DONNEES_${stationType}_${toUpper(module.module)}`
    const foundParameter = parameters.find(p => p.parameter === parameter)?.value
    const foundModuleParameter = moduleParameters.find(p => p.parameter === parameter)?.value

    const defaultDisplayMode = useMemo(() => {
        switch (stationType) {
            case FREQUENCY_STATIONS_TYPES.piezo:
                return DEFAULT_DISPLAY_MODES
            case FREQUENCY_STATIONS_TYPES.hydro:
                return DEFAULT_DISPLAY_MODES
            case FREQUENCY_STATIONS_TYPES.pluvio:
                return DEFAULT_PLUVIO_DISPLAY_MODES
            default:
                return undefined
        }
    }, [])

    const displayModes = useMemo(() => foundParameter ? JSON.parse(foundParameter) : defaultDisplayMode, [])
    const moduleDisplayModes = useMemo(() => foundModuleParameter ? JSON.parse(foundModuleParameter) : defaultDisplayMode, [])

    const [settingsEl, setSettingsEl] = useState(undefined)
    const [tempDisplayModes, setTempDiplayModes] = useState(displayModes)
    const [optionDayHourSelected, setOptionDayHourSelected] = useState(HOUR)
    const [hoursCumul, setHoursCumul] = useState(1)
    const [optionCumulSelected, setOptionCumulSelected] = useState(CUMUL_PERSO_MAX)

    const [moduleSettingsEl, setModuleSettingsEl] = useState(undefined)
    const [moduleTempDisplayModes, setModuleTempDiplayModes] = useState(moduleDisplayModes)
    const [moduleOptionDayHourSelected, setModuleOptionDayHourSelected] = useState(HOUR)
    const [moduleHoursCumul, setModuleHoursCumul] = useState(1)
    const [moduleOptionCumulSelected, setModuleOptionCumulSelected] = useState(CUMUL_PERSO_MAX)

    useDebounce(() => {
        setParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalParameter(parameter, JSON.stringify(tempDisplayModes)),
        ])
    }, 200, [tempDisplayModes])

    useDebounce(() => {
        setModuleParameters(prev => [
            ...prev.filter(p => p.parameter !== parameter),
            getGlobalModuleParameter(parameter, JSON.stringify(moduleTempDisplayModes)),
        ])
    }, 200, [moduleTempDisplayModes])

    // Pluvio : multisélection des modes accessibles (fusion simple) + on garde un `default` valide
    // (un mode accessible) ; sinon on retombe sur le 1er accessible. Autres types : inchangé.
    const withValidPluvioDefault = merged => {
        const accessible = keys(omit(merged, ['personalizedGroupingValue', 'default'])).filter(k => merged[k])
        const validDefault = accessible.includes(merged.default) ? merged.default : (accessible[0] || 'auto')
        return { ...merged, default: validDefault }
    }

    const onChangeDisplayMode = (changes, changes2 = {}) => {
        const newDisplay = stationType === FREQUENCY_STATIONS_TYPES.pluvio
            ? withValidPluvioDefault({ ...tempDisplayModes, ...changes, ...changes2 })
            : (() => {
                const tmpNewDisplay = { ...tempDisplayModes, ...changes, ...changes2 }
                return {
                    ...tmpNewDisplay,
                    auto: (!tmpNewDisplay.brute && !tmpNewDisplay.min && !tmpNewDisplay.max && !tmpNewDisplay.average && !tmpNewDisplay.personalizedGrouping),
                }
            })()
        setTempDiplayModes(newDisplay)
    }

    const onChangeModuleDisplayMode = (changes, changes2 = {}) => {
        const newDisplay = stationType === FREQUENCY_STATIONS_TYPES.pluvio
            ? withValidPluvioDefault({ ...moduleTempDisplayModes, ...changes, ...changes2 })
            : (() => {
                const tmpNewDisplay = { ...moduleTempDisplayModes, ...changes, ...changes2 }
                return {
                    ...tmpNewDisplay,
                    auto: (!tmpNewDisplay.brute && !tmpNewDisplay.min && !tmpNewDisplay.max && !tmpNewDisplay.average && !tmpNewDisplay.personalizedGrouping),
                }
            })()
        setModuleTempDiplayModes(newDisplay)
    }

    useEffect(() => {
        if (displayModes.personalizedGrouping) {
            const groupingValues = displayModes.personalizedGroupingValue.split('_')
            const [cumulValues, dayValues] = chunk(groupingValues, 3)
            const dayHour = parseInt(dayValues[0])
            const cumulSelected = cumulValues.join('_')
            setHoursCumul(dayHour)
            setOptionCumulSelected(cumulSelected)
        }
    }, [displayModes])

    useEffect(() => {
        if (moduleDisplayModes.personalizedGrouping) {
            const groupingValues = moduleDisplayModes.personalizedGroupingValue.split('_')
            const [cumulValues, dayValues] = chunk(groupingValues, 3)
            const dayHour = parseInt(dayValues[0])
            const cumulSelected = cumulValues.join('_')
            setModuleHoursCumul(dayHour)
            setModuleOptionCumulSelected(cumulSelected)
        }
    }, [moduleDisplayModes])

    useEffect(() => {
        if (tempDisplayModes?.personalizedGrouping) {
            const day = `${optionCumulSelected}_${hoursCumul * 24}`
            const hour = `${optionCumulSelected}_${hoursCumul}`
            onChangeDisplayMode({ personalizedGrouping: true }, { personalizedGroupingValue: optionDayHourSelected === DAY ? day : hour })
        }
    }, [optionDayHourSelected, hoursCumul, optionCumulSelected])

    useEffect(() => {
        if (moduleTempDisplayModes?.personalizedGrouping) {
            const day = `${moduleOptionCumulSelected}_${moduleHoursCumul * 24}`
            const hour = `${moduleOptionCumulSelected}_${moduleHoursCumul}`
            onChangeModuleDisplayMode({ personalizedGrouping: true }, { personalizedGroupingValue: moduleOptionDayHourSelected === DAY ? day : hour })
        }
    }, [moduleOptionDayHourSelected, moduleHoursCumul, moduleOptionCumulSelected])

    return (
        <Grid container justifyContent='space-between' alignItems='center' columnSpacing={1}>
            <Grid size={0.5} />
            <Grid container size='grow'>
                <Typography variant='normalFormTitle'>{i18n.defaultDataFrequency}</Typography>
            </Grid>
            <Grid size={3.25}>
                <Chip
                    label={i18n.editFrequencies}
                    icon={<EditRoundedIcon />}
                    onClick={event => setSettingsEl(event.currentTarget)}
                    clickable
                    variant='new-filled'
                    sx={{
                        color: mainBlue,
                        backgroundColor: `rgba(${brutRgbMainBlue}, 0.06)`,
                        backgroundColorHover: `rgba(${brutRgbMainBlue}, 0.16)`,
                    }}
                    disabled={!isGlobal && displayModuleParameter}
                />
                <Popover
                    open={!!settingsEl}
                    anchorEl={settingsEl}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                width: '350px',
                                maxHeight: MAX_POPOVER_HEIGHT,
                                padding: '1rem',
                                border: `solid 1px rgba(${brutRgbMainBlue}, 0.6)`,
                                borderRadius: LARGE_RADIUS,
                            },
                        },
                    }}
                    onClose={() => setSettingsEl(undefined)}
                >
                    <FrequencyDatasChoices
                        stationType={stationType}
                        defaultDisplayMode={defaultDisplayMode}

                        displayModes={tempDisplayModes}
                        setDisplayModes={onChangeDisplayMode}

                        optionDayHourSelected={optionDayHourSelected}
                        setOptionDayHourSelected={setOptionDayHourSelected}
                        hoursCumul={hoursCumul}
                        setHoursCumul={setHoursCumul}
                        optionCumulSelected={optionCumulSelected}
                        setOptionCumulSelected={setOptionCumulSelected}
                    />
                </Popover>
            </Grid>
            {!!displayModuleParameter && (
                <Grid size={3.25}>
                    <Chip
                        label={i18n.editFrequencies}
                        icon={<EditRoundedIcon />}
                        onClick={event => setModuleSettingsEl(event.currentTarget)}
                        clickable
                        variant='new-filled'
                        sx={{
                            color: mainBlue,
                            backgroundColor: `rgba(${brutRgbMainBlue}, 0.06)`,
                            backgroundColorHover: `rgba(${brutRgbMainBlue}, 0.16)`,
                        }}
                    />
                    <Popover
                        open={!!moduleSettingsEl}
                        anchorEl={moduleSettingsEl}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    width: '350px',
                                    maxHeight: MAX_POPOVER_HEIGHT,
                                    padding: '1rem',
                                    border: `solid 1px rgba(${brutRgbMainBlue}, 0.6)`,
                                    borderRadius: LARGE_RADIUS,
                                },
                            },
                        }}
                        onClose={() => setModuleSettingsEl(undefined)}
                    >
                        <FrequencyDatasChoices
                            stationType={stationType}
                            defaultDisplayMode={defaultDisplayMode}

                            displayModes={moduleTempDisplayModes}
                            setDisplayModes={onChangeModuleDisplayMode}

                            optionDayHourSelected={moduleOptionDayHourSelected}
                            setOptionDayHourSelected={setModuleOptionDayHourSelected}
                            hoursCumul={moduleHoursCumul}
                            setHoursCumul={setModuleHoursCumul}
                            optionCumulSelected={moduleOptionCumulSelected}
                            setOptionCumulSelected={setModuleOptionCumulSelected}
                        />
                    </Popover>
                </Grid>
            )}
            <Grid offset={!displayModuleParameter && !isGlobal && 3.25} size={0.5} />
        </Grid>
    )
}

FrequencyDatas.propTypes = {
    module: PropTypes.shape({
        module: PropTypes.string,
    }),
    stationType: PropTypes.string,
    parameters: PropTypes.arrayOf(PropTypes.shape({})),
    setParameters: PropTypes.func,
    moduleParameters: PropTypes.arrayOf(PropTypes.shape({})),
    setModuleParameters: PropTypes.func,
    getGlobalParameter: PropTypes.func,
    getGlobalModuleParameter: PropTypes.func,
    displayModuleParameter: PropTypes.bool,
    isGlobal: PropTypes.bool,
}

export default FrequencyDatas
